import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail, postForm } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ArrowLeft, Send, Save, Loader2, Trash2, Plus, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "../../lib/expenseOptions";
import { todayIST } from "../../lib/datetime";
import { SELF_SERVICE_ROLES } from "../../lib/roleOptions";

const UNLINKED = "__none__";

// A claim is a HEADER plus LINE ITEMS (RC-26). A cap is per category, so a
// single-amount claim cannot be checked against one: 1,500 typed `Doctor Gift`
// and 1,000 of travel plus 500 of gift are the same number and different facts.
const EMPTY_LINE = {
  // No hardcoded default category. The list is Director-editable
  // (settings_expense_types) and the previous default, "Travel", stopped being
  // a valid value the moment the categories migration renamed it — every
  // submission failed "Unknown expense_type" regardless of amount (#78).
  expense_type: "",
  amount: "",
  receipt_note: "",
  // Server-owned, never sent: only an upload that passed the backend's
  // type/magic/size checks and reached R2 sets this.
  id: null,
  receipt_url: null,
};

let lineKeySeq = 0;
const newLine = () => ({ ...EMPTY_LINE, key: `line-${lineKeySeq++}` });

const EMPTY = {
  expense_date: todayIST(),
  description: "",
  linked_doctor_id: "",
};

// Per-line testids stay stable for the first line, which is the claim every
// existing test and every one-line claim files.
const tid = (base, index) => (index === 0 ? base : `${base}-${index}`);

export default function ExpenseFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [params] = useSearchParams();
  const isEdit = !!editId;
  const [form, setForm] = useState({ ...EMPTY });
  const [lines, setLines] = useState([newLine()]);
  const [doctors, setDoctors] = useState([]);
  // The categories `POST /expenses` will actually accept, from the same
  // function the validator calls (`active_expense_types` via `/expenses/
  // meta`) — never a frontend-owned list, which is what drifted out of sync
  // with the backend and made every submission fail.
  const [expenseTypes, setExpenseTypes] = useState([]);
  // AND THE CAPS, from the same endpoint, for the same reason (#78, #83): they
  // are Director-editable data. `null` means "not loaded" and is NOT the same
  // as `{}` ("nothing is capped") — there is no fallback map, because a
  // fallback is used precisely when the fetch failed, which is the worst
  // moment to guess a ceiling on somebody's money.
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState("");
  // `null` while unknown (loading, or the fetch failed) — the routing hint
  // below stays hidden rather than guessing a number, since a wrong or stale
  // threshold shown next to the amount is worse than no hint at all.
  const [threshold, setThreshold] = useState(null);
  const fileInputs = useRef({});
  // THE CLAIM'S RECEIPTS. `pendingFiles` are chosen but not yet uploaded —
  // they travel with the save/submit in one request. `savedReceipts` are the
  // ones already stored (an edit, or a second selection after a save).
  const [pendingFiles, setPendingFiles] = useState([]);
  const [savedReceipts, setSavedReceipts] = useState([]);
  // The limits the SERVER enforces, served from /expenses/meta. No frontend
  // copy: a form holding its own idea of the size cap or the accepted types
  // is the #78 shape, and it goes stale the moment the server changes.
  const [meta, setMeta] = useState(null);
  const claimFileInput = useRef(null);
  // The testid of the input that failed validation, so it can be ringed. Null
  // whenever the form is clean or the error is not about one field.
  const [invalidField, setInvalidField] = useState(null);

  // The accept list comes from the server's own RECEIPT_TYPES. Empty until
  // meta loads, which leaves the picker unrestricted for a moment rather than
  // asserting a list the server might not agree with — the backend validates
  // every file regardless, so this is a convenience, not the guard.
  const acceptAttr = (meta?.receipt_content_types || []).join(",");

  useEffect(() => {
    // Managers file their own expenses too. Guarding on sales_officer alone sent
    // them straight back to /expenses, so "+ Log expense" looked like it did
    // nothing but silently landed on the Team view.
    if (user && !SELF_SERVICE_ROLES.includes(user.role)) {
      navigate("/expenses", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get("/expenses/meta")
      .then(({ data }) => {
        setMeta(data);
        setExpenseTypes(data.expense_types || []);
        // `?? null`, not `|| {}`: a response with no caps at all is a server
        // that cannot tell us the ceilings, and reading that as "nothing is
        // capped" is the fallback-shaped guess this rule exists to forbid. It
        // blocks filing and says so.
        setCaps(data.category_caps ?? null);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        // Own doctors only. An unscoped /doctors also returns a manager's team's
        // doctors, and the backend rejects linking one that isn't theirs.
        const { data } = await api.get("/doctors", { params: { assigned_to: user.id } });
        setDoctors(data);
        const preset = params.get("doctor");
        if (!isEdit && preset && data.find((d) => d.id === preset)) {
          setForm((p) => ({ ...p, linked_doctor_id: preset }));
        }
      } catch (_) {}
    })();
  }, [isEdit, params, user?.id]);

  useEffect(() => {
    // Only a Sales Officer's OWN chain changes with the amount — an escalated
    // claim's Admin-Manager-then-Director route only exists as a variant of
    // `sales_officer`'s chain (see `approval_chain.ESCALATED_CHAIN`). A Sales
    // Manager's or Admin Manager's own expense routes the same regardless of
    // amount, so the fetch and the hint are both skipped for them — showing an
    // amount-tied hint that never actually changes would be decorative at
    // best and misleading if it read as a promise about routing it isn't.
    if (user?.role !== "sales_officer") return;
    api.get("/settings/approval-policy")
      .then(({ data }) => setThreshold(data.expense_escalation_threshold))
      .catch(() => {}); // informational only — never blocks filing a claim
  }, [user?.role]);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/expenses/${editId}`);
        if (cancelled) return;
        setForm({
          expense_date: data.expense_date,
          description: data.description || "",
          linked_doctor_id: data.linked_doctor_id || "",
        });
        setSavedReceipts(data.receipts || []);
        const stored = data.line_items?.length
          ? data.line_items
          // A row the line-item migration has not reached yet is the one line
          // item it always was, rendered as such rather than as an empty form.
          : [{ id: null, expense_type: data.expense_type, amount: data.amount,
               receipt_note: data.receipt_note }];
        setLines(stored.map((item) => ({
          ...newLine(),
          id: item.id || null,
          expense_type: item.expense_type || "",
          amount: item.amount == null ? "" : String(item.amount),
          receipt_note: item.receipt_note || "",
          receipt_url: item.receipt_url || null,
        })));
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId]);

  function update(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function updateLine(index, key, value) {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, [key]: value } : line)));
  }

  function addLine() {
    setLines((prev) => [...prev, newLine()]);
  }

  function removeLine(index) {
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  const total = useMemo(
    () => lines.reduce((sum, l) => sum + (Number(l.amount) > 0 ? Number(l.amount) : 0), 0),
    [lines]
  );

  // WHICH CATEGORIES THIS CLAIM IS OVER. Per category, summed across the lines
  // — two ₹125 Miscellaneous lines are ₹250 of Miscellaneous, which is the
  // whole reason the backend sums them too. This blocks the round trip; the
  // BACKEND is the enforcement (`_enforce_caps` refuses the write), and this
  // never sends a claim the server would refuse.
  const breaches = useMemo(() => {
    if (!caps) return [];
    const totals = {};
    for (const line of lines) {
      const amount = Number(line.amount);
      if (!line.expense_type || !Number.isFinite(amount) || amount <= 0) continue;
      totals[line.expense_type] = (totals[line.expense_type] || 0) + amount;
    }
    return Object.entries(totals)
      .filter(([category, claimed]) => caps[category] != null && claimed > caps[category])
      .map(([category, claimed]) => ({ category, claimed, cap: caps[category] }));
  }, [lines, caps]);

  // Returns `null` when the claim is fileable, otherwise
  // `{message, field}` — the field being the testid of the input at fault.
  // A message alone is not enough: on a phone the offending input can be
  // several screens above the button, and "Field required" beside the button
  // tells a rep nothing about WHERE to look. Prashant filled Details and the
  // linked doctor and still could not tell what was missing.
  function validate() {
    if (!form.expense_date) {
      return { message: "Pick the expense date.", field: "expense-input-date" };
    }
    // NO FALLBACK. If the categories or the caps could not be loaded, filing is
    // blocked and the error is shown — guessing either one is how #78 shipped.
    if (!expenseTypes.length || caps == null) {
      return { message: "Expense categories could not be loaded, so this claim cannot be filed yet. Reload the page.", field: null };
    }
    for (const [i, line] of lines.entries()) {
      // "Category 2" rather than "line 2": the button says "Add another
      // category", so that is the word the rep has in mind.
      const label = lines.length > 1 ? ` for category ${i + 1}` : "";
      if (!line.expense_type) {
        return { message: `Pick the expense type${label}.`,
                 field: tid("expense-input-type", i) };
      }
      const amt = Number(line.amount);
      if (!Number.isFinite(amt) || amt <= 0) {
        return { message: `Enter an amount greater than zero${label}.`,
                 field: tid("expense-input-amount", i) };
      }
    }
    if (!form.description.trim()) {
      return { message: "Add the details of this expense.",
               field: "expense-input-description" };
    }
    if (breaches.length) {
      return {
        message: breaches
          .map((b) => `${b.category} is capped at ${formatINR(b.cap)} per claim; this claim comes to ${formatINR(b.claimed)}.`)
          .join(" "),
        field: null,
      };
    }
    return null;
  }

  async function onSubmit(submitDirect) {
    setError("");
    setInvalidField(null);
    const v = validate();
    if (v) {
      setError(v.message);
      setInvalidField(v.field);
      // BRING THE REP TO THE FIELD. An error message that names the field is
      // still useless if the field is off-screen, which on a phone it usually
      // is. `block: "center"` so the input is not hidden under a sticky
      // header; focus so the keyboard opens on the thing that needs typing.
      if (v.field) {
        const node = document.querySelector(`[data-testid="${v.field}"]`);
        if (node) {
          node.scrollIntoView?.({ behavior: "smooth", block: "center" });
          node.focus?.({ preventScroll: true });
        }
      }
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        expense_date: form.expense_date,
        description: form.description.trim(),
        // ALWAYS line items, even when there is one — one shape reaches the
        // backend, so the caps see one population and not two.
        line_items: lines.map((line) => ({
          ...(line.id ? { id: line.id } : {}),
          expense_type: line.expense_type,
          amount: Number(line.amount),
          receipt_note: line.receipt_note.trim() || null,
        })),
        linked_doctor_id:
          form.linked_doctor_id && form.linked_doctor_id !== UNLINKED ? form.linked_doctor_id : null,
      };
      if (isEdit) {
        const { data } = await api.put(`/expenses/${editId}`, payload);
        // Bills chosen during THIS edit go up before the claim leaves Draft —
        // once submitted it can no longer be edited, receipts included.
        if (pendingFiles.length) {
          const body = new FormData();
          pendingFiles.forEach((file) => body.append("files", file));
          const uploaded = await postForm(`/expenses/${editId}/receipts`, body);
          setSavedReceipts(uploaded.data.receipts || []);
          setPendingFiles([]);
        }
        if (submitDirect && data.status === "Draft") {
          await api.put(`/expenses/${editId}/submit`);
        }
        toast.success(submitDirect ? "Submitted" : "Saved as draft");
        navigate(`/expenses/${editId}`);
      } else if (pendingFiles.length) {
        // ONE ACTION: the claim and every bill in a single request. The
        // backend validates all files before creating anything and removes
        // the claim if storage fails, so there is no half-filed state to
        // explain to a rep — see create_expense_with_receipts.
        const body = new FormData();
        body.append(
          "payload",
          JSON.stringify({ ...payload, submit: submitDirect })
        );
        pendingFiles.forEach((file) => body.append("files", file));
        const { data } = await postForm("/expenses/with-receipts", body);
        setPendingFiles([]);
        toast.success(
          submitDirect
            ? `Expense submitted with ${data.receipt_count} receipt(s)`
            : `Draft saved with ${data.receipt_count} receipt(s)`
        );
        navigate(`/expenses/${data.id}`);
      } else {
        const { data } = await api.post("/expenses", { ...payload, submit: submitDirect });
        toast.success(submitDirect ? "Expense submitted" : "Draft saved");
        navigate(`/expenses/${data.id}`);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  function onClaimFilesPicked(fileList) {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    setError("");
    // ACCUMULATE. A phone camera returns one bill per capture, so replacing
    // the list on every selection would silently drop the previous bill —
    // which is exactly the "nothing is silently dropped" requirement.
    setPendingFiles((previous) => {
      const combined = [...previous, ...chosen];
      const limit = meta?.receipt_max_files;
      if (limit && combined.length + savedReceipts.length > limit) {
        setError(
          `You can attach at most ${limit} receipts to one claim. ` +
            `Remove one before adding another.`
        );
        return previous;
      }
      return combined;
    });
    // Clear the input so picking the SAME file again still fires onChange.
    if (claimFileInput.current) claimFileInput.current.value = "";
  }

  function onRemovePendingFile(index) {
    setPendingFiles((previous) => previous.filter((_, i) => i !== index));
  }

  async function onRemoveSavedReceipt(receiptId) {
    setError("");
    try {
      const { data } = await api.delete(
        `/expenses/${editId}/receipts/${receiptId}`
      );
      setSavedReceipts(data.receipts || []);
      toast.success("Receipt removed");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function onReceiptPicked(index, file) {
    if (!file) return;
    const line = lines[index];
    setError("");
    setUploading(index);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await postForm(
        `/expenses/${editId}/line-items/${line.id}/receipt`,
        body
      );
      const stored = (data.line_items || []).find((i) => i.id === line.id);
      updateLine(index, "receipt_url", stored?.receipt_url || null);
      toast.success("Receipt attached");
    } catch (e) {
      // The backend's refusal verbatim — it names the reason (wrong type, not
      // really a PDF, too large) or says R2 is not configured and that the file
      // was NOT stored. Never softened into "upload failed".
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(null);
      if (fileInputs.current[index]) fileInputs.current[index].value = "";
    }
  }

  async function onDelete() {
    if (!isEdit) return;
    if (!confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await api.delete(`/expenses/${editId}`);
      toast.success("Draft deleted");
      navigate("/expenses", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-callisto-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  // The rule this reflects: `approval_chain.ESCALATED_CHAIN` only has an entry
  // for `sales_officer`, so `threshold` is only ever fetched (and this hint
  // only ever shown) for that role — see the effect above. `> `, not `>=`:
  // strictly greater is the rule (routes_settings.DEFAULT_APPROVAL_POLICY),
  // so a claim of exactly the threshold does not escalate.
  const willEscalate =
    threshold != null && Number.isFinite(total) && total > 0 ? total > threshold : null;

  return (
    <div className="max-w-2xl mx-auto" data-testid="expense-form-page">
      <Link
        to="/expenses"
        className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy"
      >
        <ArrowLeft size={14} />
        Back
      </Link>
      <div className="mt-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
          {isEdit ? "Edit draft" : "Log expense"}
        </span>
        <h1 className="mt-1 text-2xl md:text-3xl font-bold text-callisto-navy">
          {isEdit ? "Edit expense" : "New expense"}
        </h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(true);
        }}
        className="mt-5 bg-white border border-callisto rounded-xl p-5 md:p-7 space-y-5"
      >
        <div>
          <Label htmlFor="edate" className="text-callisto-navy font-medium">
            Date <span className="text-callisto-gold">*</span>
          </Label>
          <Input
            id="edate"
            type="date"
            max={todayIST()}
            value={form.expense_date}
            onChange={(e) => update("expense_date", e.target.value)}
            className="h-12 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            aria-invalid={invalidField === "expense-input-date" || undefined}
                    data-testid="expense-input-date"
          />
        </div>

        {lines.map((line, index) => {
          const cap = caps?.[line.expense_type];
          const lineAmount = Number(line.amount);
          const overCap =
            cap != null &&
            breaches.some((b) => b.category === line.expense_type);
          return (
            <div
              key={line.key}
              className="rounded-lg border border-callisto/70 p-4 space-y-4"
              data-testid={tid("expense-line", index)}
            >
              {lines.length > 1 && (
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-callisto-muted">
                    Line {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeLine(index)}
                    className="text-xs text-red-700 hover:underline"
                    data-testid={`expense-line-remove-${index}`}
                  >
                    Remove
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-callisto-navy font-medium">
                    Type <span className="text-callisto-gold">*</span>
                  </Label>
                  <Select
                    value={line.expense_type}
                    onValueChange={(v) => updateLine(index, "expense_type", v)}
                  >
                    <SelectTrigger
                      className="h-12 mt-1.5 focus:ring-callisto-gold"
                      aria-invalid={invalidField === tid("expense-input-type", index) || undefined}
                    data-testid={tid("expense-input-type", index)}
                    >
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseTypes.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* The cap, from the backend, next to the category it caps —
                      so the ceiling is visible BEFORE the claim is refused. */}
                  {cap != null && (
                    <p
                      className="mt-1 text-[11px] text-callisto-muted"
                      data-testid={tid("expense-cap-note", index)}
                    >
                      Capped at {formatINR(cap)} per claim.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-callisto-navy font-medium">
                    Amount (₹) <span className="text-callisto-gold">*</span>
                  </Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={line.amount}
                    onChange={(e) => updateLine(index, "amount", e.target.value)}
                    placeholder="0"
                    className="h-12 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                    aria-invalid={invalidField === tid("expense-input-amount", index) || undefined}
                    data-testid={tid("expense-input-amount", index)}
                  />
                  {overCap && (
                    <p
                      className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5"
                      data-testid={tid("expense-cap-breach", index)}
                    >
                      {line.expense_type} is capped at {formatINR(cap)} per claim.
                      This claim comes to{" "}
                      {formatINR(
                        breaches.find((b) => b.category === line.expense_type).claimed
                      )}{" "}
                      — it will be refused.
                    </p>
                  )}
                  {!overCap && Number.isFinite(lineAmount) && lineAmount > 0 && (
                    <p className="mt-1 text-xs text-callisto-muted">
                      {formatINR(lineAmount)}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-callisto-navy font-medium">
                  Receipt note <span className="text-callisto-muted font-normal">(optional)</span>
                </Label>
                <Input
                  value={line.receipt_note}
                  onChange={(e) => updateLine(index, "receipt_note", e.target.value)}
                  placeholder="Voucher #, bill #, etc."
                  className="h-12 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid={tid("expense-input-receipt", index)}
                />
                {/* NO per-line file input. The bills attach ONCE, to the
                    claim, through the single control below the lines — a rep
                    at a counter scans travel, food and marketing bills in one
                    go and should not hunt for an input per line. The
                    per-line-item upload endpoint still exists server-side and
                    is still tested; nothing in the form drives it. */}
                {line.receipt_url && (
                  <a
                    href={fileUrl(line.receipt_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-callisto-gold hover:underline"
                    data-testid={tid("expense-receipt-link", index)}
                  >
                    <Paperclip size={12} /> Receipt attached to this line
                  </a>
                )}
              </div>
            </div>
          );
        })}

        {/* ONE CONTROL PER CLAIM (18 Aug ruling). It sits here, below the
            lines and above the actions, so it is on the path between filling
            the form and pressing Submit — the old flow put no control on the
            create form at all and a rep could Submit having never seen one.

            `multiple` and `capture` together: the rep is standing at a counter
            with a phone and paper bills, so the camera must be one tap away,
            and several bills must go in one selection. Selections ACCUMULATE
            rather than replace, because picking twice is how a phone camera
            captures two bills. */}
        <div className="rounded-xl border border-callisto-line bg-white p-4">
          <Label className="text-callisto-navy font-medium">
            Receipts{" "}
            <span className="text-callisto-muted font-normal">
              (photos or PDFs of your bills)
            </span>
          </Label>
          <p className="mt-1 text-[11px] text-callisto-muted">
            Attach every bill for this claim here — one place for all the lines
            above. You can take photos one after another.
          </p>

          <input
            type="file"
            multiple
            accept={acceptAttr}
            capture="environment"
            ref={claimFileInput}
            onChange={(e) => onClaimFilesPicked(e.target.files)}
            className="mt-3 block w-full text-xs"
            data-testid="expense-claim-receipt-file"
          />

          {/* WHAT IS ATTACHED SO FAR, always shown, so nothing is silently
              dropped. Pending files are listed before they exist server-side —
              that is the whole point of a one-action submit. */}
          {(pendingFiles.length > 0 || savedReceipts.length > 0) && (
            <ul className="mt-3 space-y-1.5" data-testid="expense-receipt-list">
              {savedReceipts.map((receipt) => (
                <li
                  key={receipt.id}
                  className="flex items-center justify-between gap-2 text-xs"
                  data-testid={`expense-receipt-saved-${receipt.id}`}
                >
                  <a
                    href={fileUrl(receipt.receipt_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-callisto-gold hover:underline truncate"
                  >
                    <Paperclip size={12} /> {receipt.filename || "Receipt"}
                  </a>
                  <button
                    type="button"
                    onClick={() => onRemoveSavedReceipt(receipt.id)}
                    className="text-callisto-muted hover:text-red-600 shrink-0"
                    data-testid={`expense-receipt-remove-${receipt.id}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
              {pendingFiles.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between gap-2 text-xs text-callisto-navy"
                  data-testid={`expense-receipt-pending-${index}`}
                >
                  <span className="inline-flex items-center gap-1 truncate">
                    <Paperclip size={12} /> {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemovePendingFile(index)}
                    className="text-callisto-muted hover:text-red-600 shrink-0"
                    data-testid={`expense-receipt-pending-remove-${index}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-2 text-[11px] text-callisto-muted">
            {pendingFiles.length + savedReceipts.length} attached
            {meta?.receipt_max_files ? ` of ${meta.receipt_max_files} allowed` : ""}
            . They are uploaded when you save or submit — one step, no separate
            attach.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={addLine}
            className="h-10 border-callisto"
            data-testid="expense-add-line"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add another category
          </Button>
          <p className="text-sm text-callisto-muted">
            Claim total:{" "}
            <span className="font-bold text-callisto-navy" data-testid="expense-claim-total">
              {formatINR(total)}
            </span>
          </p>
        </div>

        {willEscalate === true && (
          <p
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
            data-testid="expense-escalation-hint"
          >
            This exceeds the {formatINR(threshold)} threshold — it will go
            to the Admin Manager, then the Director, skipping your Sales
            Manager.
          </p>
        )}
        {willEscalate === false && (
          <p className="text-[11px] text-callisto-muted" data-testid="expense-escalation-hint">
            This will go to your Sales Manager, then the Admin Manager.
          </p>
        )}

        <div>
          <Label htmlFor="desc" className="text-callisto-navy font-medium">
            Details <span className="text-callisto-gold">*</span>
          </Label>
          <Textarea
            id="desc"
            rows={3}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="What was this expense for?"
            className="mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            aria-invalid={invalidField === "expense-input-description" || undefined}
                    data-testid="expense-input-description"
          />
        </div>

        <div>
          <Label className="text-callisto-navy font-medium">
            Linked doctor <span className="text-callisto-muted font-normal">(optional)</span>
          </Label>
          <Select
            value={form.linked_doctor_id || UNLINKED}
            onValueChange={(v) => update("linked_doctor_id", v === UNLINKED ? "" : v)}
          >
            <SelectTrigger
              className="h-12 mt-1.5 focus:ring-callisto-gold"
              data-testid="expense-input-doctor"
            >
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNLINKED}>None</SelectItem>
              {doctors.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} — {d.specialization}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[11px] text-callisto-muted">
            Only your assigned doctors are shown.
          </p>
        </div>

        {error && (
          <div
            className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
            data-testid="expense-form-error"
          >
            {error}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          {isEdit && (
            <Button
              type="button"
              variant="outline"
              onClick={onDelete}
              className="h-11 border-red-200 text-red-700 hover:bg-red-50"
              data-testid="expense-form-delete"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete draft
            </Button>
          )}
          <div className="flex gap-3 sm:ml-auto">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => onSubmit(false)}
              className="h-11 border-callisto"
              data-testid="expense-form-save-draft"
            >
              <Save className="h-4 w-4 mr-1.5" />
              Save draft
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold sm:min-w-32"
              data-testid="expense-form-submit"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1.5" />
                  Submit
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
